import React from "react";
import { TRACKING_WINDOW_DAYS } from "../../../hooks/insights/insightDerive";

/**
 * The coaching signals a roster row shows.
 *
 * The trailing slot is shared with the unread badge and with the message
 * preview beside it, so this reports at most two signals rather than every flag
 * that happens to be set. A row with nothing urgent to say renders nothing.
 *
 * Renders only non-focusable `<span>`s: rows are a `<button>` in browse mode and
 * a `<label>` wrapping a checkbox in the dock's selectable mode, where a `<div>`
 * is invalid markup and a focusable child steals the row toggle.
 */

/** Background + text pairs, all existing tokens. */
const TONE_CLASSES = {
  danger: "bg-danger-bg text-danger-dark",
  warn: "bg-internal-bg text-internal-text",
  neutral: "bg-bc-neut-bg text-bc-neut",
  info: "bg-info-bg text-info",
};

const dayPhrase = (days) => (days === 1 ? "1 day" : `${days} days`);

/**
 * The coaching signals a row shows, most urgent first. At most two.
 *
 * Diet and workout are reported separately when both need attention: picking
 * only the older one hid the other, and a coach filtering by "workout plan
 * overdue" could see a row labelled with the *diet* age and no visible reason
 * for the match.
 *
 * The cap of two is what keeps this honest — the row's second line is shared
 * with the message preview, and a third chip starts eating the words that say
 * what the conversation was about. Diet and workout are the only pair that can
 * co-occur; New and Quiet describe the client rather than a plan and only
 * surface when neither plan needs anything.
 *
 * @param {Object} tags - From `deriveTags`.
 * @returns {Array<{id: string, label: string, tone: string, title: string, rank: number}>}
 */
export function pickSignals(tags) {
  if (!tags?.hasData) return [];

  // Nothing at all is one fact, not two. "No diet" + "No workout" says the same
  // thing twice and costs the row twice the space.
  if (tags.noPlan) {
    return [
      {
        id: "noPlan",
        label: "No plan",
        tone: "danger",
        title: "No diet or workout plan has ever been assigned",
        rank: Number.POSITIVE_INFINITY,
      },
    ];
  }

  const planSignal = (kind, never, stale, ageDays) => {
    if (never) {
      return {
        id: `no${kind}Plan`,
        label: `No ${kind.toLowerCase()}`,
        tone: "danger",
        title: `No ${kind.toLowerCase()} plan has ever been assigned`,
        // Never-assigned outranks any age, so it always leads a pair.
        rank: Number.POSITIVE_INFINITY,
      };
    }
    if (!stale) return null;
    return {
      id: `${kind.toLowerCase()}Stale`,
      label: `${kind} ${ageDays}d`,
      tone: "warn",
      title: `${kind} plan assigned ${dayPhrase(ageDays)} ago`,
      rank: ageDays,
    };
  };

  const plans = [
    planSignal("Diet", tags.noDietPlan, tags.dietStale, tags.dietAgeDays),
    planSignal("Workout", tags.noWorkoutPlan, tags.workoutStale, tags.workoutAgeDays),
  ].filter(Boolean);

  if (plans.length) {
    // Oldest first. A stable sort keeps diet ahead of workout when both are
    // "never assigned" or exactly the same age.
    return [...plans].sort((a, b) => b.rank - a.rank);
  }

  if (tags.isNew) {
    return [
      {
        id: "new",
        label: "New",
        tone: "info",
        title: `Attached to you ${dayPhrase(tags.attachedDaysAgo)} ago`,
        rank: 0,
      },
    ];
  }

  if (tags.notTracking) {
    return [
      {
        id: "notTracking",
        label: "Quiet",
        tone: "neutral",
        title: `No food or workout logs in the last ${TRACKING_WINDOW_DAYS} days`,
        rank: 0,
      },
    ];
  }

  return [];
}

/**
 * @param {Object} props
 * @param {Object} props.tags - From `deriveTags`.
 * @param {'sm'|'md'} [props.size='sm'] - `md` is the mobile sheet's slightly larger row.
 */
export function RosterSignalChip({ tags, size = "sm" }) {
  const signals = pickSignals(tags);
  if (!signals.length) return null;

  const sizeClass =
    size === "md" ? "px-[7px] py-0.5 text-[10.5px]" : "px-1.5 py-0.5 text-[10px]";

  return (
    <span className="inline-flex flex-shrink-0 items-center gap-1">
      {signals.map((signal) => (
        <span
          key={signal.id}
          title={signal.title}
          aria-label={signal.title}
          className={`inline-flex items-center rounded-[5px] font-bold ${sizeClass} ${
            TONE_CLASSES[signal.tone] || TONE_CLASSES.neutral
          }`}
        >
          {signal.label}
        </span>
      ))}
    </span>
  );
}

export default RosterSignalChip;
